import { Drawer, Flex, Menu, Popover, Text } from "../../components";
import { useApp, useAuth, useNavigate, useSearchParams } from "../../hooks";
import { Tools } from "../../utils";
import React, { useEffect } from "react";
import {
  ContainerOutlined,
  LogoutOutlined,
  CloseOutlined,
  HomeOutlined,
  UserOutlined,
  HeartOutlined,
} from "@ant-design/icons";

const UserInfo = ({
  type = "",
  className = "",
}: {
  type?: string;
  className?: string;
}) => {
  const { user, logout, login, isAuthenticated } = useAuth();
  const { modal } = useApp();
  const { searchParams, setSearchParams, removeParams } = useSearchParams({
    account: false,
  });
  const { navigate } = useNavigate();
  const isDrawer = type === "drawer";
  const alias = user?.alias || "Anonymous";

  const onClose = () => removeParams("account");

  const onSelectMenu = async (_: any) => {
    if (_.key === "logout") {
      modal.confirm({
        title: "Log out?",
        description: "You will need to sign in again to continue.",
        confirmText: "Log out",
        onConfirm: () => {
          onClose();
          logout();
        },
      });
      return;
    }
    onClose();
    navigate(_.key);
  };

  useEffect(() => {
    if (!isAuthenticated) onClose();
  }, [isAuthenticated]);

  const renderMenu = (forDrawer = false) => (
    <Menu
      style={{ border: "none" }}
      onSelect={onSelectMenu}
      className={forDrawer ? "user-drawer-menu" : undefined}
      items={[
        {
          key: "profile",
          label: "Profile",
          icon: <UserOutlined />,
        },
        {
          key: "orders",
          label: "Orders",
          icon: <ContainerOutlined />,
        },
        {
          key: "addresses",
          label: "Addresses",
          icon: <HomeOutlined />,
        },
        {
          key: "favorites",
          label: "Favorites",
          icon: <HeartOutlined />,
        },
        {
          key: "logout",
          danger: true,
          label: "Logout",
          icon: <LogoutOutlined />,
        },
      ]}
    />
  );

  return (
    <div className={className}>
      {!isAuthenticated ? (
        <Text
          level={5}
          weight={"700"}
          type="tertiary"
          children="Login"
          className="cursor-pointer whitespace-nowrap"
          onClick={async () => {
            try {
              await login();
            } catch {}
          }}
        />
      ) : isDrawer ? (
        <UserOutlined
          style={{
            fontSize: "2.2rem",
            color: "var(--color-on-secondary)",
          }}
          onClick={() => setSearchParams({ account: true })}
        />
      ) : (
        <Popover
          arrow
          destroyTooltipOnHide
          content={renderMenu(false)}
          placement="bottomRight"
          classNames={{ body: "p-0 overflow-hidden wr-16" }}
          children={
            <Flex className="cursor-pointer" gap={".7rem"} align="center">
              <UserOutlined
                style={{
                  fontSize: "2.2rem",
                  color: "var(--color-on-secondary)",
                }}
              />
              <Text
                level={5}
                type="tertiary"
                children={Tools.toPascalCase(alias.split(" ")[0])}
              />
            </Flex>
          }
        />
      )}
      {isDrawer && (
        <Drawer
          destroyOnClose
          open={!!searchParams.account}
          closable={false}
          placement="right"
          title={Tools.toPascalCase(alias)}
          children={renderMenu(true)}
          onClose={onClose}
          extra={
            <Text
              level={4}
              color="secondary"
              className="cursor-pointer"
              onClick={onClose}
              children={<CloseOutlined />}
            />
          }
          classNames={{
            header: "user-drawer-header",
            body: "user-drawer-body",
          }}
        />
      )}
    </div>
  );
};

export default React.memo(UserInfo);
